import { useEffect, useState, type KeyboardEvent, type MouseEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, ExternalLink, Plus, RefreshCw } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";

import { Badge, type BadgeTone } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input, Label } from "@/components/ui/Input";
import { apiFetch } from "@/lib/api";
import { buildMegaventorySalesOrderUrl } from "@/lib/megaventory";
import { buildInsideSalesApprovalPath, buildInsideSalesSalesOrderEditPath } from "@/lib/hubEntityDetailPaths";
import { formatSalesOrderRepFieldLabel } from "@/lib/salesOrderRepFieldDisplay";
import { salesOrderStatusLabel } from "@/lib/salesOrderHubStatus";
import { useIntegratorMe, isIntegratorAdmin } from "@/lib/me-query";
import { useHubModuleAdminSettings, useHubModuleWrite } from "@/lib/useHubModuleWrite";
import { cn } from "@/lib/utils";

import { SalesOrderDownloadMenu } from "./SalesOrderDownloadMenu";
import { SalesOrderLinkApprovalModal } from "./SalesOrderLinkApprovalModal";
import { insideSalesUserMayDownloadSalesOrderPdf } from "./salesOrderPdfDownloadAccess";

interface SalesOrderOverviewResponse {
  summary: {
    totalCount: number;
    thisWeekTotalValue: number;
    thisMonthTotalValue: number;
  };
  orders: {
    id: number;
    number: string;
    status: string | null;
    submitted: string | null;
    shipByDate: string | null;
    customerPurchaseOrderNumber: string | null;
    paymentTerms: string | null;
    clientName: string | null;
    endUserName: string | null;
    salesManagerName: string | null;
    repAgencyName: string | null;
    repStaffName: string | null;
    itemCount: number;
    totalQuantity: number;
    orderValue: number;
    mvId: string | null;
    approvalTicketId: string | null;
    invoices: {
      mvId: string;
      number: string;
    }[];
    items: {
      id: number;
      sku: string;
      quantity: number;
      unitPrice: number;
      remark: string | null;
    }[];
  }[];
}

const STATUS_OPTIONS = ["All", "Draft", "Pending", "Approved", "Closed", "FullyInvoiced", "Cancelled"];
const SEARCH_DEBOUNCE_MS = 250;

const currencyFormatter = new Intl.NumberFormat(undefined, {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});

const numberFormatter = new Intl.NumberFormat();

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = window.setTimeout(() => setDebounced(value), delayMs);
    return () => window.clearTimeout(timer);
  }, [delayMs, value]);
  return debounced;
}

function formatCurrency(value: number): string {
  return currencyFormatter.format(value);
}

function formatDate(value: string | null): string {
  if (!value) return "Not set";
  const normalized = value.slice(0, 10);
  const [year, month, day] = normalized.split("-");
  if (!year || !month || !day) return value;
  return `${month}/${day}/${year}`;
}

function statusTone(status: string | null): BadgeTone {
  const label = salesOrderStatusLabel(status);
  if (label === "Cancelled") return "danger";
  if (label === "Draft") return "info";
  if (label === "Pending") return "warning";
  if (label === "Closed" || label === "FullyInvoiced") return "success";
  if (label === "Approved") return "success";
  return "neutral";
}

function stopCardNavigation(event: MouseEvent | KeyboardEvent) {
  event.stopPropagation();
}

export function SalesOrderOverview() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const hubWritesAllowed = useHubModuleWrite("inside_sales");
  const meQuery = useIntegratorMe();
  const isIntegratorAdminUser = isIntegratorAdmin(meQuery.data?.user.roleId);
  const canUseInsideSalesAdminSettings = useHubModuleAdminSettings("inside_sales");
  const [searchParams] = useSearchParams();
  const initialSearch = searchParams.get("search")?.trim() ?? "";
  const [status, setStatus] = useState("All");
  const [searchDraft, setSearchDraft] = useState(initialSearch);
  const search = useDebouncedValue(searchDraft, SEARCH_DEBOUNCE_MS);
  const [linkApprovalError, setLinkApprovalError] = useState<string | null>(null);
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [linkApprovalTargetOrder, setLinkApprovalTargetOrder] = useState<{ id: number; number: string } | null>(null);
  const [expandedOrderIds, setExpandedOrderIds] = useState<Set<number>>(() => new Set());

  const overviewQuery = useQuery({
    queryKey: ["sales-orders", "overview", status, search],
    queryFn: () =>
      apiFetch<SalesOrderOverviewResponse>("/api/sales-orders/overview", {
        query: {
          status,
          search: search.trim() || undefined,
          limit: 150,
        },
      }),
  });

  const orders = overviewQuery.data?.orders ?? [];
  const summary = overviewQuery.data?.summary;

  const createApprovalMutation = useMutation({
    mutationFn: (orderId: number) =>
      apiFetch<{ approvalTicketId: string }>(`/api/sales-orders/${orderId}/approval-ticket`, { method: "POST" }),
    onSuccess: async (response) => {
      setLinkApprovalError(null);
      setLinkApprovalTargetOrder(null);
      await queryClient.invalidateQueries({ queryKey: ["sales-orders", "overview"] });
      navigate(buildInsideSalesApprovalPath(response.approvalTicketId));
    },
    onError: (error) => {
      setLinkApprovalError(error instanceof Error ? error.message : "Could not create an approval task.");
    },
  });

  const linkApprovalMutation = useMutation({
    mutationFn: ({ orderId, reference }: { orderId: number; reference: string }) =>
      apiFetch<{ approvalTicketId: string }>(`/api/sales-orders/${orderId}/link-approval`, {
        method: "POST",
        body: { reference },
      }),
    onSuccess: async () => {
      setLinkApprovalError(null);
      setLinkApprovalTargetOrder(null);
      await queryClient.invalidateQueries({ queryKey: ["sales-orders", "overview"] });
    },
    onError: (error) => {
      setLinkApprovalError(error instanceof Error ? error.message : "Could not link an approval to this sales order.");
    },
  });

  function toggleOrderItems(orderId: number) {
    setExpandedOrderIds((current) => {
      const next = new Set(current);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  }

  function openSalesOrder(orderNumber: string) {
    navigate(buildInsideSalesSalesOrderEditPath(orderNumber));
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-950">Sales Order Overview</h1>
        </div>
        <div className="flex w-full flex-col gap-3 sm:flex-row lg:w-auto lg:items-end">
          <div className="flex w-full flex-col gap-3 sm:flex-row lg:w-auto">
            <div className="min-w-44">
              <Label htmlFor="sales-order-status">Status</Label>
              <select
                id="sales-order-status"
                value={status}
                onChange={(event) => setStatus(event.target.value)}
                className="mt-1 h-9 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              >
                {STATUS_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-72 flex-1">
              <Label htmlFor="sales-order-search">Search</Label>
              <div className="mt-1 flex gap-2">
                <Input
                  id="sales-order-search"
                  value={searchDraft}
                  onChange={(event) => setSearchDraft(event.target.value)}
                  placeholder="GSO, PO, invoice, GOSI, client:Acme, @end-user Ameren, sku:88-403"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  title="Refresh sales orders"
                  onClick={() => void overviewQuery.refetch()}
                >
                  <RefreshCw className={cn("h-4 w-4", overviewQuery.isFetching ? "animate-spin" : "")} aria-hidden="true" />
                </Button>
              </div>
            </div>
          </div>
          {hubWritesAllowed ? (
            <Link to="/inside-sales/sales-orders/new" className="inline-flex">
              <Button type="button">
                <Plus className="h-4 w-4" aria-hidden="true" />
                <span className="sm:hidden">New</span>
                <span className="hidden sm:inline">New sales order</span>
              </Button>
            </Link>
          ) : null}
        </div>
      </div>

      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle>Orders</CardTitle>
            <p className="mt-1 text-sm text-slate-500">
              {overviewQuery.isLoading
                ? "Loading orders"
                : `${numberFormatter.format(orders.length)} shown${summary ? ` of ${numberFormatter.format(summary.totalCount)}` : ""}`}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
              <div className="text-[11px] font-semibold uppercase tracking-normal text-slate-500">Order Price Total This Week</div>
              <div className="mt-1 text-sm font-semibold text-slate-900">
                {formatCurrency(summary?.thisWeekTotalValue ?? 0)}
              </div>
            </div>
            <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
              <div className="text-[11px] font-semibold uppercase tracking-normal text-slate-500">Order Price Total This Month</div>
              <div className="mt-1 text-sm font-semibold text-slate-900">
                {formatCurrency(summary?.thisMonthTotalValue ?? 0)}
              </div>
            </div>
          </div>
        </CardHeader>
        <CardBody className="space-y-4">
          {overviewQuery.isError ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-5 text-sm text-red-700">
              {overviewQuery.error instanceof Error ? overviewQuery.error.message : "Could not load sales orders."}
            </div>
          ) : null}
          {linkApprovalError ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-5 text-sm text-red-700">{linkApprovalError}</div>
          ) : null}
          {downloadError ? (
            <div className="rounded-md border border-red-200 bg-red-50 p-5 text-sm text-red-700">{downloadError}</div>
          ) : null}
          {!overviewQuery.isError && orders.length === 0 && !overviewQuery.isLoading ? (
            <div className="p-1 text-sm text-slate-500">No sales orders match the current filters.</div>
          ) : null}
          {orders.map((order) => {
            const megaventoryOrderUrl = buildMegaventorySalesOrderUrl(order.mvId);
            const isExpanded = expandedOrderIds.has(order.id);
            const orderPath = buildInsideSalesSalesOrderEditPath(order.number);

            return (
            <div key={order.id} className="rounded-lg border border-slate-200 bg-white">
              <div
                className={cn(
                  "flex cursor-pointer items-start gap-3 bg-slate-50 px-5 py-4 transition-colors hover:bg-slate-100",
                  isExpanded ? "border-b border-slate-200" : "rounded-lg",
                )}
                role="link"
                tabIndex={0}
                aria-label={`Open sales order ${order.number}`}
                onClick={() => openSalesOrder(order.number)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" || event.key === " ") {
                    event.preventDefault();
                    openSalesOrder(order.number);
                  }
                }}
              >
                <div className="grid min-w-0 flex-1 gap-x-5 gap-y-4 sm:grid-cols-2 lg:grid-cols-4 2xl:grid-cols-8">
                  <div>
                    <div className="flex flex-wrap items-center gap-2" onClick={stopCardNavigation} onKeyDown={stopCardNavigation}>
                      <Link to={orderPath} className="font-semibold text-slate-950 hover:text-brand-700">
                        {order.number}
                      </Link>
                      {megaventoryOrderUrl ? (
                        <a
                          href={megaventoryOrderUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50"
                          onClick={stopCardNavigation}
                        >
                          Megaventory
                          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                        </a>
                      ) : null}
                      {order.approvalTicketId ? (
                        <Link
                          to={buildInsideSalesApprovalPath(order.approvalTicketId)}
                          className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 transition-colors hover:bg-slate-50"
                          onClick={stopCardNavigation}
                        >
                          View approval
                          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                        </Link>
                      ) : hubWritesAllowed ? (
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          className="h-7 px-2 text-xs"
                          onClick={(event) => {
                            stopCardNavigation(event);
                            setLinkApprovalError(null);
                            setLinkApprovalTargetOrder({ id: order.id, number: order.number });
                          }}
                        >
                          Link approval
                        </Button>
                      ) : null}
                      <SalesOrderDownloadMenu
                        orderId={order.id}
                        orderNumber={order.number}
                        invoices={order.invoices ?? []}
                        canDownloadSalesOrderPdf={insideSalesUserMayDownloadSalesOrderPdf({
                          isPendingOrder: (order.status ?? "").trim().toLowerCase() === "pending",
                          isIntegratorAdmin: isIntegratorAdminUser,
                          isInsideSalesAdmin: canUseInsideSalesAdminSettings,
                        })}
                        stopCardNavigation
                        onError={setDownloadError}
                      />
                    </div>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      <Badge tone={statusTone(order.status)}>{salesOrderStatusLabel(order.status)}</Badge>
                      {order.customerPurchaseOrderNumber ? (
                        <Badge tone="muted">PO {order.customerPurchaseOrderNumber}</Badge>
                      ) : null}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-normal text-slate-600">Customer</div>
                    <div className="mt-1 font-semibold text-slate-950">{order.endUserName ?? "No end user"}</div>
                    <div className="mt-1 text-sm text-slate-600">{order.clientName ?? "Unknown client"}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-normal text-slate-600">Submitted</div>
                    <div className="mt-1 text-sm font-medium text-slate-800">{formatDate(order.submitted)}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-normal text-slate-600">Ship by</div>
                    <div className="mt-1 text-sm font-medium text-slate-800">{formatDate(order.shipByDate)}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-normal text-slate-600">Payment terms</div>
                    <div className="mt-1 text-sm font-medium text-slate-800">{order.paymentTerms ?? "Not set"}</div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-normal text-slate-600">SGS sales manager</div>
                    <div className="mt-1 text-sm font-medium text-slate-800">
                      {formatSalesOrderRepFieldLabel(order.salesManagerName) || "Not set"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-normal text-slate-600">Rep agency</div>
                    <div className="mt-1 text-sm font-medium text-slate-800">
                      {formatSalesOrderRepFieldLabel(order.repAgencyName) || "Not set"}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-normal text-slate-600">Rep agent</div>
                    <div className="mt-1 text-sm font-medium text-slate-800">
                      {formatSalesOrderRepFieldLabel(order.repStaffName) || "Not set"}
                    </div>
                  </div>
                </div>
                <button
                  type="button"
                  className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-slate-300 bg-white text-slate-600 transition-colors hover:bg-slate-50 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-brand-500"
                  aria-expanded={isExpanded}
                  aria-label={isExpanded ? `Hide items for ${order.number}` : `Show items for ${order.number}`}
                  title={isExpanded ? "Hide sales order items" : "Show sales order items"}
                  onClick={(event) => {
                    event.stopPropagation();
                    toggleOrderItems(order.id);
                  }}
                >
                  <ChevronDown
                    className={cn("h-4 w-4 transition-transform", isExpanded ? "rotate-180" : "")}
                    aria-hidden="true"
                  />
                </button>
              </div>
              {isExpanded ? (
                <div className="px-5 py-4">
                  <div className="mb-3 text-xs font-medium uppercase tracking-normal text-slate-500">Sales order items</div>
                  <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="text-left text-xs font-semibold uppercase tracking-normal text-slate-500">
                      <tr>
                        <th className="pb-2 pr-4">SKU</th>
                        <th className="pb-2 pr-4 text-right">Qty</th>
                        <th className="pb-2 pr-4 text-right">Unit price</th>
                        <th className="pb-2">Remark</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {order.items.map((item) => (
                        <tr key={item.id}>
                          <td className="py-2 pr-4 font-medium text-slate-900">{item.sku}</td>
                          <td className="py-2 pr-4 text-right tabular-nums text-slate-700">
                            {numberFormatter.format(item.quantity)}
                          </td>
                          <td className="py-2 pr-4 text-right tabular-nums text-slate-700">
                            {formatCurrency(item.unitPrice)}
                          </td>
                          <td className="py-2 text-slate-600">{item.remark && item.remark.trim() !== "" ? item.remark : "-"}</td>
                        </tr>
                      ))}
                      {order.items.length > 0 ? (
                        <tr className="bg-slate-50/70">
                          <td className="py-2 pr-4 font-semibold text-slate-900">Summary</td>
                          <td className="py-2 pr-4 text-right font-semibold tabular-nums text-slate-900">
                            {numberFormatter.format(order.totalQuantity)}
                          </td>
                          <td className="py-2 pr-4 text-right text-slate-500">
                            {numberFormatter.format(order.itemCount)} lines
                          </td>
                          <td className="py-2 font-semibold text-slate-900">{formatCurrency(order.orderValue)}</td>
                        </tr>
                      ) : null}
                      {order.items.length === 0 ? (
                        <tr>
                          <td colSpan={4} className="py-2 text-slate-500">
                            No sales_order_item rows attached.
                          </td>
                        </tr>
                      ) : null}
                    </tbody>
                  </table>
                  </div>
                </div>
              ) : null}
            </div>
          )})}
        </CardBody>
      </Card>
      {linkApprovalTargetOrder ? (
        <SalesOrderLinkApprovalModal
          orderLabel={linkApprovalTargetOrder.number}
          isLinking={linkApprovalMutation.isPending}
          isCreating={createApprovalMutation.isPending}
          linkError={linkApprovalError}
          onClose={() => {
            if (!linkApprovalMutation.isPending && !createApprovalMutation.isPending) {
              setLinkApprovalError(null);
              setLinkApprovalTargetOrder(null);
            }
          }}
          onLink={(reference) =>
            linkApprovalMutation.mutate({ orderId: linkApprovalTargetOrder.id, reference })
          }
          onCreateNew={() => createApprovalMutation.mutate(linkApprovalTargetOrder.id)}
        />
      ) : null}
    </div>
  );
}
